import { useCallback, useEffect, useRef, useState } from 'react';
import { getAuthzenSubject } from './subject';
import { buildAuthZENAction } from './authz-operation-refs';
import { inferPdpApplication } from './authz-constants';

// Global in-memory cache shared across all hook instances (5-minute TTL)
const globalAuthzCache = new Map<string, { allowed: boolean; ts: number; promise?: Promise<boolean> }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function defaultResourceIdForType(resourceType: string): string {
  // Keep UI-surface authz canonical: (type, id) pairs should match the ID format guide.
  switch (resourceType) {
    case 'experience.route':
      return 'route:experience:/';
    case 'experience.plugin':
      return 'plugin:experience';
    case 'experience.tab':
      return 'tab:experience:root';
    case 'experience.action':
      return 'action:experience:root';
    default:
      return 'NoResource';
  }
}

export function useAuthorization(resource?: string, action?: string, eager: boolean = true, resourceId?: string) {
  const [authorized, setAuthorized] = useState<boolean>(!(resource && action));
  const [loading, setLoading] = useState<boolean>(!!(resource && action && eager));

  const evaluate = useCallback(async (res: string, act: string, resId?: string): Promise<boolean> => {
    let subject;
    try {
      subject = await getAuthzenSubject();
    } catch (err) {
      // FAIL CLOSED NOISILY: Identity governance requires known subject for all authz checks
      console.error('[IGA:AUTHZ] useAuthorization: Subject resolution failed — failing closed.', err);
      return false;
    }
    const resolvedResourceId = (resId && resId.trim().length > 0) ? resId : defaultResourceIdForType(res);
    const subjectKey = subject.id || 'anon';
    const cacheKey = `${subjectKey}::${res}::${resolvedResourceId}::${act}`;
    
    // Check global cache first
    const cached = globalAuthzCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      // If there's a pending promise, return it to deduplicate concurrent requests
      if (cached.promise) return cached.promise;
      return cached.allowed;
    }
    
    // Try persistent cache (cross-refresh)
    try {
      const raw = localStorage.getItem(`authz:v1:${cacheKey}`);
      if (raw) {
        const { allowed, ts } = JSON.parse(raw || '{}') as { allowed?: boolean; ts?: number };
        if (typeof allowed === 'boolean' && typeof ts === 'number' && Date.now() - ts < CACHE_TTL_MS) {
          globalAuthzCache.set(cacheKey, { allowed, ts });
          return allowed;
        }
      }
    } catch {}
    
    // Create and cache the promise to deduplicate concurrent requests
    const evaluationPromise = (async () => {
      const csrf = (document.cookie.match(/(?:^|; )_eid_csrf_v1=([^;]+)/)?.[1]) || '';
      const actionObj = buildAuthZENAction(res, act);
      const pdpApp = inferPdpApplication(res);
      const response = await fetch('/access/v1/evaluation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrf,
          'X-CSRFToken': csrf,
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
        body: JSON.stringify({
          subject,
          resource: { type: res, id: resolvedResourceId, properties: { pdp_application: pdpApp } },
          action: actionObj,
          context: { pdp_application: pdpApp }
        })
      });
      const ok = response.ok && (await response.json()).decision === true;
      const now = Date.now();
      globalAuthzCache.set(cacheKey, { allowed: ok, ts: now });
      try { localStorage.setItem(`authz:v1:${cacheKey}`, JSON.stringify({ allowed: ok, ts: now })); } catch {}
      return ok;
    })();
    
    // Store promise temporarily for deduplication
    globalAuthzCache.set(cacheKey, { allowed: false, ts: Date.now(), promise: evaluationPromise });
    const result = await evaluationPromise;
    // Remove promise after completion
    const entry = globalAuthzCache.get(cacheKey);
    if (entry) delete entry.promise;
    return result;
  }, []);

  useEffect(() => {
    if (eager && resource && action) {
      let mounted = true;
      setLoading(true);
      evaluate(resource, action, resourceId).then(allow => mounted && setAuthorized(allow)).finally(() => mounted && setLoading(false));
      return () => { mounted = false; };
    }
  }, [evaluate, resource, action, eager, resourceId]);

  return { can: evaluate, authorized, loading } as const;
}


